import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUserCity } from './useUserCity';
import config from '../../../../config/config';
import Fetch from '../../../../util/core/Fetch';
import { getUserSession } from '../../../../util/core/Session';
import { buildRequestFilters, shouldEnableQuery } from '../Util/addCustomFilters';
import { useReportAvailablePeriod } from '../contexts/AvailablePeriodContext';

export const useChartsData = (queryConfig, filters) => {
    const { data, isLoading } = useUserCity();
    const session = getUserSession();

    const profile = session.user.application_profile_id;

    const isAdm = profile === config.default_foreign_key.profiles.zellusAdmin;

    const isClientManager =
        profile === config.default_foreign_key.sdg_profiles.clientManager;

    // Usuário tem cidade específica vinculada
    const hasUserCity = !!data?.city_id;

    // Usuário deve usar filtro de cidade (admin, manager, ou sem cidade específica)
    const shouldUseCityFilter = isAdm || isClientManager || !hasUserCity;

    const enabled = shouldUseCityFilter ? !!filters?.city : hasUserCity && !isLoading;

    const city = filters?.city || data?.city_id;

    const query = useQuery({
        queryKey: [queryConfig.key, ...Object.values(filters)],
        queryFn: async ({ signal }) => {
            const requestFilters = buildRequestFilters(queryConfig, filters, city);

            return await Fetch.post(
                config.endpoints.backend + queryConfig.url,
                requestFilters,
                signal
            );

        },
        enabled: shouldEnableQuery(queryConfig, filters, enabled),
        staleTime: 1000 * 60 * 60
    });

    // Alimenta o período disponível com as competências que o backend devolveu.
    const reportPeriod = useReportAvailablePeriod();

    useEffect(() => {
        if (query.isSuccess) {
            reportPeriod(query.data, city);
        }
    }, [query.isSuccess, query.data, city, reportPeriod]);

    return query;
};


const AGE_GROUP = 'age-group-services';
const COMPLEXETY_LEVEL = 'complexity-level-services';
const GENDER = 'gender-services';
const PRODUCTION_SITUATION = 'production-situation-services';
const RACE_COLOR = 'race-color-services';
const GENERAL_RESULTS = 'general-results-services';
const DISCHARGE = 'discharge-services';
const OUTS = 'outs-services';
const SERVICES = 'services';
const PRODUCTION_VALUE = 'production-value-services';
const PRODUCTION_QUANTITY = 'production-quantity-services';
const INCREASE_IN_VALUES = 'increase-in-values-services';
const PRINCIPAL_CID = 'principal-cid-services';
const PROCEDURES = 'procedures-services';
const CITY = 'city-services';

export const QUERY_KEYS_SIA = {
    AGE_GROUP,
    COMPLEXETY_LEVEL,
    GENDER,
    PRODUCTION_SITUATION,
    RACE_COLOR,
    GENERAL_RESULTS,
    DISCHARGE,
    OUTS,
    SERVICES,
    PRODUCTION_VALUE,
    PRODUCTION_QUANTITY,
    INCREASE_IN_VALUES,
    PRINCIPAL_CID,
    PROCEDURES,
    CITY
};

const GENERAL_RESULTS_AIH_SIH = 'general-results-aih-sih';
const CHARACTER_OF_ADMISSSION_AIH_SIH = 'character-of-admission-aih-sih';
const SPECIALITIES_AIH_SIH = 'specialities-aih-sih';
const ETHNICITY_PERFIL_SIH = 'ethnicity-perfil-sih';
const AGE_GROUP_PERFIL_SIH = 'age-group-perfil-sih';
const GENDER_PERFIL_SIH = 'gender-perfil-sih';
const EDUCATION_PERFIL_SIH = 'education-perfil-sih';
const CITY_PERFIL_SIH = 'city-perfil-sih';
const COMPLEXETY_LEVEL_AIH_SIH = 'complexity-level-aih-sih';
const PERMANENCE_DAYS_HOSPITALIZATION_SIH ='permanence-days-hospitalization_sih';
const TOTALS_HOSPITALIZATION_SIH = 'totals-hospitalization-sih';
const TOTAL_UTILIZATION_MONTH_SIH = 'total-utilization-month-uti-sih';
const INTERMIDIATE_CARE_UNIT_SIH = 'intermidiate-care-unit-uti-sih';
const UTI_TYPE_SIH = 'uti-type-sih';
const UTI_VALUE_SIH = 'uti-value-uti-sih';
const IND_VDLR_SIH = 'ind-vdlr_procedures-sih';
const HOSPITAL_INFECTION_SIH = 'hospital-infection-procedures-sih';
const PREGANT_SIH = 'pregnant-procedures-sih';
const DEATH_TYPE_SIH = 'death-discharge-sih';
const PROCEDURES_LIST_SIH = 'procedures-list-procedures-sih';
const FINANCING_TYPE_REVENUE_SIH = 'financing-type-revenue-sih';
const FEDERAL_REVENUE_SIH = 'federal-revenue-sih';
const MANAGEMENT_REVENUE_AIH = 'management-revenue-aih';
const INDICATORS_REVENUE_SIH = 'indicators-revenue-sih';
const TOTAL_REVENUE_SIH = 'total-revenue-sih';

export const QUERY_KEYS_SIH = {
    GENERAL_RESULTS_AIH: GENERAL_RESULTS_AIH_SIH,
    SPECIALITIES_AIH: SPECIALITIES_AIH_SIH,
    ETHNICITY_PERFIL: ETHNICITY_PERFIL_SIH,
    AGE_GROUP_PERFIL: AGE_GROUP_PERFIL_SIH,
    GENDER_PERFIL: GENDER_PERFIL_SIH,
    EDUCATION_PERFIL: EDUCATION_PERFIL_SIH,
    CITY_PERFIL: CITY_PERFIL_SIH,
    CHARACTER_OF_ADMISSSION: CHARACTER_OF_ADMISSSION_AIH_SIH,
    COMPLEXETY_LEVEL: COMPLEXETY_LEVEL_AIH_SIH,
    PERMANENCE_DAYS_HOSPITALIZATION: PERMANENCE_DAYS_HOSPITALIZATION_SIH,
    TOTALS_HOSPITALIZATION: TOTALS_HOSPITALIZATION_SIH,
    TOTAL_UTILIZATION_MONTH: TOTAL_UTILIZATION_MONTH_SIH,
    INTERMIDIATE_CARE_UNIT: INTERMIDIATE_CARE_UNIT_SIH,
    UTI_TYPE: UTI_TYPE_SIH,
    UTI_VALUE: UTI_VALUE_SIH,
    IND_VDLR: IND_VDLR_SIH,
    HOSPITAL_INFECTION: HOSPITAL_INFECTION_SIH,
    PREGANT: PREGANT_SIH,
    DEATH_TYPE: DEATH_TYPE_SIH,
    PROCEDURES_LIST: PROCEDURES_LIST_SIH,
    FINANCING_TYPE: FINANCING_TYPE_REVENUE_SIH,
    FEDERAL: FEDERAL_REVENUE_SIH,
    MANAGEMENT: MANAGEMENT_REVENUE_AIH,
    INDICATORS: INDICATORS_REVENUE_SIH,
    TOTAL: TOTAL_REVENUE_SIH
};

const DATABASE_MAPPING_UR = 'database-mapping-ur';
const COLLECTION_METHOD_MAPPING_UR = 'collection-method-mapping-ur';
const INFRASTRUCTURE_FAMILIES_UR = 'infrastructure-families-ur';
const FAMILIES_MAPPING_UR = 'families-mapping-ur';
const PEOPLE_MAPPING_UR = 'people-mapping-ur';
const BENIFITS_MAPPING_UR = 'benifits-mapping-ur';
const HOUSEHOLD_FAMILIES_UR = 'household-families-ur';
const BASIC_SANITATION_FAMILIES_UR = 'basic-sanitation-families-ur';
const TRADICIONAL_GROUPS_FAMILIES_UR = 'tradicional-groups-families-ur';
const HOUSEHOLD_INCOME_FAMILIES_UR = 'household-income-families-ur';
const HOUSEHOLD_EXPENSES_FAMILIES_UR = 'household-expenses-families-ur';
const PROFILE_PEOPLE_UR = 'profile-people-ur';
const HOUSEHOLD_RELATIONSHIP_PEOPLE_UR = 'household-relationship-people-ur';
const REGISTRATION_SITUATION_PEOPLE_UR = 'registration-situation-people-ur';
const PEOPLE_WITH_DISABILITIES_DISABILITY_UR = 'people-with-disabilities-disability-ur';
const STREET_SITUATION_HOMELESS_UR = 'street-situation-homeless-ur';

export const QUERY_KEYS_UR = {
    DATABASE_MAPPING: DATABASE_MAPPING_UR,
    COLLECTION_METHOD_MAPPING: COLLECTION_METHOD_MAPPING_UR,
    INFRASTRUCTURE_FAMILIES: INFRASTRUCTURE_FAMILIES_UR,
    FAMILIES_MAPPING: FAMILIES_MAPPING_UR,
    PEOPLE_MAPPING: PEOPLE_MAPPING_UR,
    HOUSEHOLD_FAMILIES: HOUSEHOLD_FAMILIES_UR,
    BENIFITS_MAPPING: BENIFITS_MAPPING_UR,
    BASIC_SANITATION_FAMILIES: BASIC_SANITATION_FAMILIES_UR,
    TRADICIONAL_GROUPS_FAMILIES: TRADICIONAL_GROUPS_FAMILIES_UR,
    HOUSEHOLD_INCOME_FAMILIES: HOUSEHOLD_INCOME_FAMILIES_UR,
    HOUSEHOLD_EXPENSES_FAMILIES: HOUSEHOLD_EXPENSES_FAMILIES_UR,
    PROFILE_PEOPLE: PROFILE_PEOPLE_UR,
    HOUSEHOLD_RELATIONSHIP_PEOPLE: HOUSEHOLD_RELATIONSHIP_PEOPLE_UR,
    REGISTRATION_SITUATION_PEOPLE: REGISTRATION_SITUATION_PEOPLE_UR,
    PEOPLE_WITH_DISABILITIES_DISABILITY: PEOPLE_WITH_DISABILITIES_DISABILITY_UR,
    STREET_SITUATION_HOMELESS: STREET_SITUATION_HOMELESS_UR
};

const RESULTS_SAMU = 'dashboardsamu/encounters/results';
const SAMU_CALLS_RESULTS = 'dashboardsamu/results/calls';
const REGULATION_RESULTS_VS = 'dashboardsamu/results/regulation-results';
const APH_RESULTS_VS = 'dashboardsamu/results/aph';
const PERIOD_RESULTS_VS = 'dashboardsamu/results/period';
const PRIORITY_SAMU = 'dashboardsamu/results/priority';
const PROFILE_RESULTS = 'dashboardsamu/results/profile';
const SECTORIZATION_RESULTS = 'dashboardsamu/results/sectorization';
const SPECIAL_CARE_RESULTS = 'dashboardsamu/results/special-care';

export const QUERY_KEYS_SAMU = {
    RESULTS: RESULTS_SAMU,
    CALLS: SAMU_CALLS_RESULTS,
    REGULATION_RESULTS: REGULATION_RESULTS_VS,
    APH_RESULTS: APH_RESULTS_VS,
    PERIOD_RESULTS: PERIOD_RESULTS_VS,
    PRIORITY: PRIORITY_SAMU,
    PROFILE: PROFILE_RESULTS,
    SECTORIZATION_RESULTS: SECTORIZATION_RESULTS,
    SPECIAL_CARE: SPECIAL_CARE_RESULTS
};

const GENERAL_RESULTS_RESULTS_VS = 'general-results-results-vs';
const MORTALITY_MOBIDITY_RESULTS_VS = 'mortality-mobidity-results-vs';
const ATTENTION_RESULTS_VS = 'attention-results-vs';
const RESULTS_LIVE_BIRTHS_VS = 'results-live-births-vs';
const IDENTIFICATION_LIVE_BIRTHS_VS = 'identification-live-births-vs';
const OCCURENCE_LOCATION_LIVE_BIRTHS_VS = 'occurence-location-live-births-vs';
const PARENTS_INFO_LIVE_BIRTHS_VS = 'parents-info-live-births-vs';
const PREVIOUS_PREGNANCIES_LIVE_BIRTHS_VS = 'previous-pregnancies-live-births-vs';
const CURRENT_PREGNANCY_LIVE_BIRTHS_VS = 'current-pregnancy-live-births-vs';
const BIRTH_LIVE_BIRTHS_VS = 'birth-live-births-vs';
const RESULTS_MORTALITY_VS = 'results-mortality-vs';
const IDENTIFICATION_MORTALITY_VS = 'identification-mortality-vs';
const OCCURENCE_LOCATION_MORTALITY_VS = 'occurence-location-mortality-vs';
const FETAL_DEATH_MORTALITY_VS = 'fetal-death-mortality-vs';
const CONDITIONS_CAUSES_MORTALITY_VS = 'conditions-causes-mortality-vs';

export const QUERY_KEYS_VS = {
    GENERAL_RESULTS_RESULTS: GENERAL_RESULTS_RESULTS_VS,
    MORTALITY_MOBIDITY_RESULTS: MORTALITY_MOBIDITY_RESULTS_VS,
    ATTENTION_RESULTS: ATTENTION_RESULTS_VS,
    RESULTS_LIVE_BIRTHS: RESULTS_LIVE_BIRTHS_VS,
    IDENTIFICATION_LIVE_BIRTHS: IDENTIFICATION_LIVE_BIRTHS_VS,
    OCCURENCE_LOCATION_LIVE_BIRTHS: OCCURENCE_LOCATION_LIVE_BIRTHS_VS,
    PARENTS_INFO_LIVE_BIRTHS: PARENTS_INFO_LIVE_BIRTHS_VS,
    PREVIOUS_PREGNANCIES_LIVE_BIRTHS: PREVIOUS_PREGNANCIES_LIVE_BIRTHS_VS,
    CURRENT_PREGNANCY_LIVE_BIRTHS: CURRENT_PREGNANCY_LIVE_BIRTHS_VS,
    BIRTH_LIVE_BIRTHS: BIRTH_LIVE_BIRTHS_VS,
    RESULTS_MORTALITY: RESULTS_MORTALITY_VS,
    IDENTIFICATION_MORTALITY: IDENTIFICATION_MORTALITY_VS,
    OCCURENCE_LOCATION_MORTALITY: OCCURENCE_LOCATION_MORTALITY_VS,
    FETAL_DEATH_MORTALITY: FETAL_DEATH_MORTALITY_VS,
    CONDITIONS_CAUSES_MORTALITY: CONDITIONS_CAUSES_MORTALITY_VS
};

const GENERAL_RESULTS_IGD = 'general-results-igd';
const SCHOOL_ATTENDANCE_IGD = 'school-attendance-igd';
const HEALTH_SCHEDULE_IGD = 'health-schedule-igd';
const REGISTRATION_UPDATES_IGD = 'registration-updates-igd';
const VALUES_RESULTS_IGD = 'values-results-igd';
const FAMILY_BREACHS_OF_CONDITIONS_IGD = 'family-breaches-of-conditions-igd';
const TOTAL_INCENTIVES_IGD = 'total-incentives-igd';
const CALCULATED_VALUE_WITH_INCENTIVES_IGD = 'calculated-value-with-incentives-igd';
const TRANSFER_VALUE_COMPARISON_IGD = 'transfer-value-comparison-igd';
const TRANSFER_VALUE_COMPARISON_PERCENTUAL_IGD = 'transfer-value-comparison-percentual-igd';
const CHILDREN_SCHOOL_ATTENDANCE_COMPARISON_IGD = 'children-school-attendance-comparison-igd';
const CHILDREN_SCHOOL_ATTENDANCE_COMPARISON_PERCENTAGE_IGD = 'children-school-attendance-comparison-percentage-igd';
const WITH_INFORMED_HEALTH_CONDITIONS_COMPARISON_IGD = 'with-informed-health-conditions-comparison-igd';
const WITH_INFORMED_HEALTH_CONDITIONS_COMPARISON_PERCENTAGE_IGD = 'with-informed-health-conditions-comparison-percentage-igd';
const PROFILE_CAD_UNICO_COMPARISON_IGD = 'profile-cad-unico-comparison-igd';
const PROFILE_CAD_UNICO_COMPARISON_PERCENTAGE_IGD = 'profile-cad-unico-comparison-percentage-igd';
const FAMILIES_CAD_UN_IGD = 'families-cad-un-igd';
const PEOPLE_DATA_CAD_UN_IGD = 'people-data-cad-un-igd';
const AVERAGE_PEOPLE_PER_FAMILY_CAD_UN_IGD = 'average-people-per-family-cad-un-igd';

export const QUERY_KEYS_IGD = {
    GENERAL_RESULTS: GENERAL_RESULTS_IGD,
    SCHOOL_ATTENDANCE: SCHOOL_ATTENDANCE_IGD,
    HEALTH_SCHEDULE: HEALTH_SCHEDULE_IGD,
    REGISTRATION_UPDATES: REGISTRATION_UPDATES_IGD,
    VALUES_RESULTS: VALUES_RESULTS_IGD,
    FAMILY_BREACHS_OF_CONDITIONS: FAMILY_BREACHS_OF_CONDITIONS_IGD,
    TOTAL_INCENTIVES: TOTAL_INCENTIVES_IGD,
    CALCULATED_VALUE_WITH_INCENTIVES: CALCULATED_VALUE_WITH_INCENTIVES_IGD,
    TRANSFER_VALUE_COMPARISON: TRANSFER_VALUE_COMPARISON_IGD,
    TRANSFER_VALUE_COMPARISON_PERCENTUAL: TRANSFER_VALUE_COMPARISON_PERCENTUAL_IGD,
    CHILDREN_SCHOOL_ATTENDANCE_COMPARISON: CHILDREN_SCHOOL_ATTENDANCE_COMPARISON_IGD,
    CHILDREN_SCHOOL_ATTENDANCE_COMPARISON_PERCENTAGE: CHILDREN_SCHOOL_ATTENDANCE_COMPARISON_PERCENTAGE_IGD,
    WITH_INFORMED_HEALTH_CONDITIONS_COMPARISON: WITH_INFORMED_HEALTH_CONDITIONS_COMPARISON_IGD,
    WITH_INFORMED_HEALTH_CONDITIONS_COMPARISON_PERCENTAGE: WITH_INFORMED_HEALTH_CONDITIONS_COMPARISON_PERCENTAGE_IGD,
    PROFILE_CAD_UNICO_COMPARISON: PROFILE_CAD_UNICO_COMPARISON_IGD,
    PROFILE_CAD_UNICO_COMPARISON_PERCENTAGE: PROFILE_CAD_UNICO_COMPARISON_PERCENTAGE_IGD,
    FAMILIES_CAD_UN: FAMILIES_CAD_UN_IGD,
    PEOPLE_DATA_CAD_UN: PEOPLE_DATA_CAD_UN_IGD,
    AVERAGE_PEOPLE_PER_FAMILY_CAD_UN: AVERAGE_PEOPLE_PER_FAMILY_CAD_UN_IGD
};

const IDCRAS_GENERAL_SAS = 'idcras-general-sas';
const IDCRAS_SIZE_SAS = 'idcras-size-sas';
const IDCRAS_STRUCTURE_SAS = 'idcras-structure-sas';
const IDCRAS_HUMAN_RESOURCES_SAS = 'idcras-human-resources-sas';
const IDCRAS_SERVICES_SAS = 'idcras-services-sas';

const IDCREAS_GENERAL_SAS = 'idcreas-general-sas';
const IDCREAS_SIZE_SAS = 'idcreas-size-sas';
const IDCREAS_STRUCTURE_SAS = 'idcreas-structure-sas';
const IDCREAS_HUMAN_RESOURCES_SAS = 'idcreas-human-resources-sas';
const IDCREAS_SERVICES_SAS = 'idcreas-services-sas';

const IDCONSELHO_GENERAL_SAS = 'idconselho-general-sas';
const IDCONSELHO_SIZE_SAS = 'idconselho-size-sas';
const IDCONSELHO_POPULATION_SAS = 'idconselho-population-sas';
const IDCONSELHO_ADMINISTRATIVE_STRUCTURE_SAS = 'idconselho-administrative-structure-sas';
const IDCONSELHO_OPERATING_DYNAMICS_SAS = 'idconselho-operating-dynamics-sas';
const IDCONSELHO_COUNCIL_COMPOSITION_SAS = 'idconselho-council-composition-sas';

export const QUERY_KEYS_SAS = {
    IDCRAS_GENERAL: IDCRAS_GENERAL_SAS,
    IDCRAS_SIZE: IDCRAS_SIZE_SAS,
    IDCRAS_STRUCTURE: IDCRAS_STRUCTURE_SAS,
    IDCRAS_HUMAN_RESOURCES: IDCRAS_HUMAN_RESOURCES_SAS,
    IDCRAS_SERVICES: IDCRAS_SERVICES_SAS,

    IDCREAS_GENERAL: IDCREAS_GENERAL_SAS,
    IDCREAS_SIZE: IDCREAS_SIZE_SAS,
    IDCREAS_STRUCTURE: IDCREAS_STRUCTURE_SAS,
    IDCREAS_HUMAN_RESOURCES: IDCREAS_HUMAN_RESOURCES_SAS,
    IDCREAS_SERVICES: IDCREAS_SERVICES_SAS,

    IDCONSELHO_GENERAL: IDCONSELHO_GENERAL_SAS,
    IDCONSELHO_SIZE: IDCONSELHO_SIZE_SAS,
    IDCONSELHO_POPULATION: IDCONSELHO_POPULATION_SAS,
    IDCONSELHO_ADMINISTRATIVE_STRUCTURE: IDCONSELHO_ADMINISTRATIVE_STRUCTURE_SAS,
    IDCONSELHO_OPERATING_DYNAMICS: IDCONSELHO_OPERATING_DYNAMICS_SAS,
    IDCONSELHO_COUNCIL_COMPOSITION: IDCONSELHO_COUNCIL_COMPOSITION_SAS,
};

const OCURRENCES = 'criminaldata-ocurrences-security';
const CRIMES_RUBRIC_EVOLUTION = 'criminaldata-crimes-rubric-evolution-security';
const CRIMES_CONDUCT_EVOLUTION = 'criminaldata-crimes-conduct-evolution-security';
const CRIMES_DETERMINED_NATURE_EVOLUTION = 'criminaldata-crimes-determined-nature-evolution-security';
const SPOTS = 'criminaldata-spots-security';
const BATTALION_EVOLUTION = 'criminaldata-battalion-evolution-security';
const COMMAND_EVOLUTION = 'criminaldata-command-evolution-security';
const COMPANY_EVOLUTION = 'criminaldata-company-evolution-security';
const RANKINGS_POLICE_STATION = 'criminaldata-rankings-police-station-security';
const RANKINGS_DEPARTMENT = 'criminaldata-rankings-department-security';
const RANKINGS_SECTIONAL = 'criminaldata-rankings-sectional-security';
const RANKINGS_CITY = 'criminaldata-rankings-city-security';
const RANKINGS_NEIGHBOURHOOD = 'criminaldata-rankings-neighbourhood-security';
const RANKINGS_BATTALION = 'criminaldata-rankings-battalion-security';
const RANKINGS_COMMAND = 'criminaldata-rankings-command-security';
const RANKINGS_COMPANY = 'criminaldata-rankings-company-security';

const PRISIONERS_OCURRENCES = 'prisioners-ocurrences-security';
const PRISIONERS_CRIMES_RUBRIC_EVOLUTION = 'prisioners-crimes-rubric-evolution-security';
const PRISIONERS_CRIMES_CONDUCT_EVOLUTION = 'prisioners-crimes-conduct-evolution-security';
const PRISIONERS_CRIMES_DETERMINED_NATURE_EVOLUTION = 'prisioners-crimes-determined-nature-evolution-security';
const PRISIONERS_SPOTS = 'prisioners-spots-security';
const PRISIONERS_RANKINGS_POLICE_STATION = 'prisioners-rankings-police-station-security';
const PRISIONERS_RANKINGS_DEPARTMENT = 'prisioners-rankings-department-security';
const PRISIONERS_RANKINGS_SECTIONAL = 'prisioners-rankings-sectional-security';
const PRISIONERS_RANKINGS_CITY = 'prisioners-rankings-city-security';
const PRISIONERS_RANKINGS_NEIGHBOURHOOD = 'prisioners-rankings-neighbourhood-security';

const RECOVERED_VEHICLES_OCURRENCES = 'recovered-vehicles-ocurrences-security';
const RECOVERED_VEHICLES_CRIMES_DETERMINED_NATURE_EVOLUTION = 'recovered-vehicles-crimes-determined-nature-evolution-security';
const RECOVERED_VEHICLES_SPOTS = 'recovered-vehicles-spots-security';
const RECOVERED_VEHICLES_RANKINGS_POLICE_STATION = 'recovered-vehicles-rankings-police-station-security';
const RECOVERED_VEHICLES_RANKINGS_DEPARTMENT = 'recovered-vehicles-rankings-department-security';
const RECOVERED_VEHICLES_RANKINGS_SECTIONAL = 'recovered-vehicles-rankings-sectional-security';
const RECOVERED_VEHICLES_RANKINGS_CITY = 'recovered-vehicles-rankings-city-security';
const RECOVERED_VEHICLES_RANKINGS_NEIGHBOURHOOD = 'recovered-vehicles-rankings-neighbourhood-security';

const WEAPONS_OCURRENCES = 'weapons-ocurrences-security';
const WEAPONS_CRIMES_DETERMINED_NATURE_EVOLUTION = 'weapons-crimes-determined-nature-evolution-security';
const WEAPONS_SPOTS = 'weapons-spots-security';
const WEAPONS_RANKINGS_POLICE_STATION = 'weapons-rankings-police-station-security';
const WEAPONS_RANKINGS_DEPARTMENT = 'weapons-rankings-department-security';
const WEAPONS_RANKINGS_SECTIONAL = 'weapons-rankings-sectional-security';
const WEAPONS_RANKINGS_CITY = 'weapons-rankings-city-security';
const WEAPONS_RANKINGS_NEIGHBOURHOOD = 'weapons-rankings-neighbourhood-security';

const NARCOTICS_QUANTITIES_OCURRENCES = 'narcotics-quantities-ocurrences-security';
const NARCOTICS_QUANTITIES_CRIMES_DETERMINED_NATURE_EVOLUTION = 'narcotics-quanticities-crimes-determined-nature-evolution-security';
const NARCOTICS_QUANTITIES_SPOTS = 'narcotics-quantities-spots-security';
const NARCOTICS_QUANTITIES_RANKINGS_POLICE_STATION = 'narcotics-quantities-rankings-police-station-security';
const NARCOTICS_QUANTITIES_RANKINGS_DEPARTMENT = 'narcotics-quantities-rankings-department-security';
const NARCOTICS_QUANTITIES_RANKINGS_SECTIONAL = 'narcotics-quantities-rankings-sectional-security';
const NARCOTICS_QUANTITIES_RANKINGS_CITY = 'narcotics-quantities-rankings-city-security';
const NARCOTICS_QUANTITIES_RANKINGS_NEIGHBOURHOOD = 'narcotics-quantities-rankings-neighbourhood-security';

const NARCOTICS_OCURRENCES = 'narcotics-ocurrences-security';
const NARCOTICS_CRIMES_DETERMINED_NATURE_EVOLUTION = 'narcotics-crimes-determined-nature-evolution-security';
const NARCOTICS_SPOTS = 'narcotics-spots-security';
const NARCOTICS_RANKINGS_POLICE_STATION = 'narcotics-rankings-police-station-security';
const NARCOTICS_RANKINGS_DEPARTMENT = 'narcotics-rankings-department-security';
const NARCOTICS_RANKINGS_SECTIONAL = 'narcotics-rankings-sectional-security';
const NARCOTICS_RANKINGS_CITY = 'narcotics-rankings-city-security';
const NARCOTICS_RANKINGS_NEIGHBOURHOOD = 'narcotics-rankings-neighbourhood-security';

const ARRESTSMADE_OCURRENCES = 'arrestsmade-ocurrences-security';
const ARRESTSMADE_CRIMES_DETERMINED_NATURE_EVOLUTION = 'arrestsmade-crimes-determined-nature-evolution-security';
const ARRESTSMADE_SPOTS = 'arrestsmade-spots-security';
const ARRESTSMADE_RANKINGS_POLICE_STATION = 'arrestsmade-rankings-police-station-security';
const ARRESTSMADE_RANKINGS_DEPARTMENT = 'arrestsmade-rankings-department-security';
const ARRESTSMADE_RANKINGS_SECTIONAL = 'arrestsmade-rankings-sectional-security';
const ARRESTSMADE_RANKINGS_CITY = 'arrestsmade-rankings-city-security';
const ARRESTSMADE_RANKINGS_NEIGHBOURHOOD = 'arrestsmade-rankings-neighbourhood-security';

const ART173_ECA_OCURRENCES = 'art173eca-ocurrences-security';
const ART173_ECA_CRIMES_DETERMINED_NATURE_EVOLUTION = 'art173eca-crimes-determined-nature-evolution-security';
const ART173_ECA_SPOTS = 'art173eca-spots-security';
const ART173_ECA_RANKINGS_POLICE_STATION = 'art173eca-rankings-police-station-security';
const ART173_ECA_RANKINGS_DEPARTMENT = 'art173eca-rankings-department-security';
const ART173_ECA_RANKINGS_SECTIONAL = 'art173eca-rankings-sectional-security';
const ART173_ECA_RANKINGS_CITY = 'art173eca-rankings-city-security';
const ART173_ECA_RANKINGS_NEIGHBOURHOOD = 'art173eca-rankings-neighbourhood-security';

const VEHICLES_OCURRENCES = 'stolen-vehicles-ocurrences';
const VEHICLES_RANKINGS_POLICE_STATION = 'stolen-vehicles-rankings-police-station';
const VEHICLES_RANKINGS_DEPARTMENT = 'stolen-vehicles-rankings-department';
const VEHICLES_RANKINGS_SECTIONAL = 'stolen-vehicles-rankings-sectional';
const VEHICLES_RANKINGS_CITY = 'stolen-vehicles-rankings-city';
const VEHICLES_RANKINGS_NEIGHBOURHOOD = 'stolen-vehicles-rankings-neighbourhood';
const VEHICLES_RANKINGS_YEAR = 'stolen-vehicles-rankings-year';
const VEHICLES_RANKINGS_VEHICLE_TYPE = 'stolen-vehicles-rankings-vehicle-type';
const VEHICLES_RANKINGS_VEHICLE_BRAND = 'stolen-vehicles-rankings-vehicle-brand';
const VEHICLES_SPOTS = 'stolen-vehicles-spots';
const VEHICLES_DETAILS_PRESENTATION = 'stolen-vehicles-details-presentation';
const VEHICLES_DETAILS_AUTORSHIP = 'stolen-vehicles-details-autorship';
const VEHICLES_DETAILS_FLAGRANT = 'stolen-vehicles-details-flagrant';
const VEHICLES_DETAILS_INFRACTIONAL = 'stolen-vehicles-details-infractional';
const VEHICLES_DETAILS_RUBRIC = 'stolen-vehicles-details-rubric';
const VEHICLES_DETAILS_CONDUCT = 'stolen-vehicles-details-conduct';
const VEHICLES_DETAILS_UNFOLDING = 'stolen-vehicles-details-unfolding';
const VEHICLES_DETAILS_CIRCUMSTANCES = 'stolen-vehicles-details-circumstances';
const VEHICLES_BRAND = 'stolen-vehicles-brand';
const VEHICLES_TYPE = 'stolen-vehicles-type';
const VEHICLES_YEAR = 'stolen-vehicles-year';
const VEHICLES_COLOR = 'stolen-vehicles-color';


const PHONES_OCURRENCES = 'phones-ocurrences';
const PHONES_RANKINGS_POLICE_STATION = 'phones-rankings-police-station';
const PHONES_RANKINGS_DEPARTMENT = 'phones-rankings-department';
const PHONES_RANKINGS_SECTIONAL = 'phones-rankings-sectional';
const PHONES_RANKINGS_CITY = 'phones-rankings-city';
const PHONES_RANKINGS_NEIGHBOURHOOD = 'phones-rankings-neighbourhood';
const PHONES_RANKINGS_SUBTYPES = 'phones-rankings-subtypes';
const PHONES_RANKINGS_TYPES = 'phones-rankings-types';
const PHONES_RANKINGS_BRANDS = 'phones-rankings-brands';
const PHONES_SPOTS = 'phones-spots';
const PHONES_DETAILS_PRESENTATION = 'phones-details-presentation';
const PHONES_DETAILS_AUTORSHIP = 'phones-details-autorship';
const PHONES_DETAILS_FLAGRANT = 'phones-details-flagrant';
const PHONES_DETAILS_INFRACTIONAL = 'phones-details-infractional';
const PHONES_DETAILS_RUBRIC = 'phones-details-rubric';
const PHONES_DETAILS_CONDUCT = 'phones-details-conduct';
const PHONES_DETAILS_UNFOLDING = 'phones-details-unfolding';
const PHONES_DETAILS_CIRCUMSTANCES = 'phones-details-circumstances';
const PHONES_TYPE = 'phone-type';
const PHONES_SUBTYPE = 'phone-subtype';
const PHONES_BRAND = 'phone-brand';
const PHONES_BLOCKSTATUS = 'phone-blockstatus';
const PHONES_QUANTITY = 'phone-quantity';

export const QUERY_KEYS_PHONES = {
    PHONES_OCURRENCES,
    PHONES_RANKINGS_POLICE_STATION,
    PHONES_RANKINGS_DEPARTMENT,
    PHONES_RANKINGS_SECTIONAL,
    PHONES_RANKINGS_CITY,
    PHONES_RANKINGS_NEIGHBOURHOOD,
    PHONES_RANKINGS_SUBTYPES,
    PHONES_RANKINGS_TYPES,
    PHONES_RANKINGS_BRANDS,
    PHONES_SPOTS,
    PHONES_DETAILS_PRESENTATION,
    PHONES_DETAILS_AUTORSHIP,
    PHONES_DETAILS_FLAGRANT,
    PHONES_DETAILS_INFRACTIONAL,
    PHONES_DETAILS_RUBRIC,
    PHONES_DETAILS_CONDUCT,
    PHONES_DETAILS_UNFOLDING,
    PHONES_DETAILS_CIRCUMSTANCES,
    PHONES_TYPE,
    PHONES_SUBTYPE,
    PHONES_BRAND,
    PHONES_BLOCKSTATUS,
    PHONES_QUANTITY
}


export const QUERY_KEYS_VEHICLES = {
    VEHICLES_OCURRENCES,
    VEHICLES_RANKINGS_POLICE_STATION,
    VEHICLES_RANKINGS_DEPARTMENT,
    VEHICLES_RANKINGS_SECTIONAL,
    VEHICLES_RANKINGS_CITY,
    VEHICLES_RANKINGS_NEIGHBOURHOOD,
    VEHICLES_RANKINGS_YEAR,
    VEHICLES_RANKINGS_VEHICLE_TYPE,
    VEHICLES_RANKINGS_VEHICLE_BRAND,
    VEHICLES_SPOTS,
    VEHICLES_DETAILS_PRESENTATION,
    VEHICLES_DETAILS_AUTORSHIP,
    VEHICLES_DETAILS_FLAGRANT,
    VEHICLES_DETAILS_INFRACTIONAL,
    VEHICLES_DETAILS_RUBRIC,
    VEHICLES_DETAILS_CONDUCT,
    VEHICLES_DETAILS_UNFOLDING,
    VEHICLES_DETAILS_CIRCUMSTANCES,
    VEHICLES_BRAND,
    VEHICLES_TYPE,
    VEHICLES_YEAR,
    VEHICLES_COLOR
};
const POLICE_INTERVENTION_DEATHS_OCURRENCES = 'police-intervention-deaths-ocurrences-security';
const POLICE_INTERVENTION_DEATHS_POLICE_BY_CORPORATE = 'police-intervention-deaths-police-by-corporate-security';
const POLICE_INTERVENTION_DEATHS_POLICE_BY_SITUATION = 'police-intervention-deaths-police-by-situation-security';
const POLICE_INTERVENTION_DEATHS_POLICE_BY_MUNICIPALITY = 'police-intervention-deaths-police-by-municipality-security';
const POLICE_INTERVENTION_DEATHS_POLICE_BY_POLICE_STATION = 'police-intervention-deaths-police-by-police-station-security';
const POLICE_INTERVENTION_DEATHS_POLICE_BY_POLICE_STATION_ELABORATION = 'police-intervention-deaths-police-by-police-station-elaboration-security';
const POLICE_INTERVENTION_DEATHS_POLICE_BY_DEPARTMENT = 'police-intervention-deaths-police-by-department-security';
const POLICE_INTERVENTION_DEATHS_SPOTS = 'police-intervention-deaths-spots-security';
const POLICE_INTERVENTION_DEATHS_RANKINGS_POLICE_STATION = 'police-intervention-deaths-rankings-police-station-security';
const POLICE_INTERVENTION_DEATHS_RANKINGS_DEPARTMENT = 'police-intervention-deaths-rankings-department-security';
const POLICE_INTERVENTION_DEATHS_RANKINGS_SECTIONAL = 'police-intervention-deaths-rankings-sectional-security';
const POLICE_INTERVENTION_DEATHS_RANKINGS_MUNICIPALITY_ELABORATION = 'police-intervention-deaths-rankings-municipality-elaboration-security';
const POLICE_INTERVENTION_DEATHS_RANKINGS_NEIGHBOURHOOD = 'police-intervention-deaths-rankings-neighbourhood-security';
const POLICE_INTERVENTION_DEATHS_POLICE_BY_DEPARTMENT_ELABORATION = 'police-intervention-deaths-police-by-department-elaboration-security';
const POLICE_INTERVENTION_DEATHS_RANKINGS_SECTIONAL_ELABORATION = 'police-intervention-deaths-rankings-sectional-elaboration-security';
const POLICE_INTERVENTION_DEATHS_PROFILE_BY_PEOPLE_TYPE = 'police-intervention-deaths-profile-by-people-type-security';
const POLICE_INTERVENTION_DEATHS_PROFILE_BY_GENDER = 'police-intervention-deaths-profile-by-gender-security';
const POLICE_INTERVENTION_DEATHS_PROFILE_BY_AGE = 'police-intervention-deaths-profile-by-age-security';
const POLICE_INTERVENTION_DEATHS_PROFILE_BY_SKIN_COLOR = 'police-intervention-deaths-profile-by-skin-color-security';
const POLICE_INTERVENTION_DEATHS_PROFILE_BY_PROFESSION = 'police-intervention-deaths-profile-by-profession-security';
const IML_OCURRENCES = 'iml-ocurrences-security';
const IML_DEATHCAUSES = 'iml-death-causes-security';

export const QUERY_KEYS_SECURITY = {
    OCURRENCES,
    CRIMES_RUBRIC_EVOLUTION,
    CRIMES_CONDUCT_EVOLUTION,
    CRIMES_DETERMINED_NATURE_EVOLUTION,
    SPOTS,
    BATTALION_EVOLUTION,
    COMMAND_EVOLUTION,
    COMPANY_EVOLUTION,
    RANKINGS_POLICE_STATION,
    RANKINGS_DEPARTMENT,
    RANKINGS_SECTIONAL,
    RANKINGS_CITY,
    RANKINGS_NEIGHBOURHOOD,
    RANKINGS_BATTALION,
    RANKINGS_COMMAND,
    RANKINGS_COMPANY,
    PRISIONERS_OCURRENCES,
    PRISIONERS_CRIMES_RUBRIC_EVOLUTION,
    PRISIONERS_CRIMES_CONDUCT_EVOLUTION,
    PRISIONERS_CRIMES_DETERMINED_NATURE_EVOLUTION,
    PRISIONERS_SPOTS,
    PRISIONERS_RANKINGS_POLICE_STATION,
    PRISIONERS_RANKINGS_DEPARTMENT,
    PRISIONERS_RANKINGS_SECTIONAL,
    PRISIONERS_RANKINGS_CITY,
    PRISIONERS_RANKINGS_NEIGHBOURHOOD,
    RECOVERED_VEHICLES_OCURRENCES,
    RECOVERED_VEHICLES_CRIMES_DETERMINED_NATURE_EVOLUTION,
    RECOVERED_VEHICLES_SPOTS,
    RECOVERED_VEHICLES_RANKINGS_POLICE_STATION,
    RECOVERED_VEHICLES_RANKINGS_DEPARTMENT,
    RECOVERED_VEHICLES_RANKINGS_SECTIONAL,
    RECOVERED_VEHICLES_RANKINGS_CITY,
    RECOVERED_VEHICLES_RANKINGS_NEIGHBOURHOOD,
    WEAPONS_OCURRENCES,
    WEAPONS_CRIMES_DETERMINED_NATURE_EVOLUTION,
    WEAPONS_SPOTS,
    WEAPONS_RANKINGS_POLICE_STATION,
    WEAPONS_RANKINGS_DEPARTMENT,
    WEAPONS_RANKINGS_SECTIONAL,
    WEAPONS_RANKINGS_CITY,
    WEAPONS_RANKINGS_NEIGHBOURHOOD,
    NARCOTICS_QUANTITIES_OCURRENCES,
    NARCOTICS_QUANTITIES_CRIMES_DETERMINED_NATURE_EVOLUTION,
    NARCOTICS_QUANTITIES_SPOTS,
    NARCOTICS_QUANTITIES_RANKINGS_POLICE_STATION,
    NARCOTICS_QUANTITIES_RANKINGS_DEPARTMENT,
    NARCOTICS_QUANTITIES_RANKINGS_SECTIONAL,
    NARCOTICS_QUANTITIES_RANKINGS_CITY,
    NARCOTICS_QUANTITIES_RANKINGS_NEIGHBOURHOOD,
    NARCOTICS_OCURRENCES,
    NARCOTICS_CRIMES_DETERMINED_NATURE_EVOLUTION,
    NARCOTICS_SPOTS,
    NARCOTICS_RANKINGS_POLICE_STATION,
    NARCOTICS_RANKINGS_DEPARTMENT,
    NARCOTICS_RANKINGS_SECTIONAL,
    NARCOTICS_RANKINGS_CITY,
    NARCOTICS_RANKINGS_NEIGHBOURHOOD,
    ARRESTSMADE_OCURRENCES,
    ARRESTSMADE_CRIMES_DETERMINED_NATURE_EVOLUTION,
    ARRESTSMADE_SPOTS,
    ARRESTSMADE_RANKINGS_POLICE_STATION,
    ARRESTSMADE_RANKINGS_DEPARTMENT,
    ARRESTSMADE_RANKINGS_SECTIONAL,
    ARRESTSMADE_RANKINGS_CITY,
    ARRESTSMADE_RANKINGS_NEIGHBOURHOOD,
    ART173_ECA_OCURRENCES,
    ART173_ECA_CRIMES_DETERMINED_NATURE_EVOLUTION,
    ART173_ECA_SPOTS,
    ART173_ECA_RANKINGS_POLICE_STATION,
    ART173_ECA_RANKINGS_DEPARTMENT,
    ART173_ECA_RANKINGS_SECTIONAL,
    ART173_ECA_RANKINGS_CITY,
    ART173_ECA_RANKINGS_NEIGHBOURHOOD,
    POLICE_INTERVENTION_DEATHS_OCURRENCES,
    POLICE_INTERVENTION_DEATHS_POLICE_BY_CORPORATE,
    POLICE_INTERVENTION_DEATHS_POLICE_BY_SITUATION,
    POLICE_INTERVENTION_DEATHS_POLICE_BY_MUNICIPALITY,
    POLICE_INTERVENTION_DEATHS_POLICE_BY_POLICE_STATION,
    POLICE_INTERVENTION_DEATHS_POLICE_BY_DEPARTMENT,
    POLICE_INTERVENTION_DEATHS_SPOTS,
    POLICE_INTERVENTION_DEATHS_RANKINGS_POLICE_STATION,
    POLICE_INTERVENTION_DEATHS_POLICE_BY_POLICE_STATION_ELABORATION,
    POLICE_INTERVENTION_DEATHS_RANKINGS_DEPARTMENT,
    POLICE_INTERVENTION_DEATHS_POLICE_BY_DEPARTMENT_ELABORATION,
    POLICE_INTERVENTION_DEATHS_RANKINGS_SECTIONAL,
    POLICE_INTERVENTION_DEATHS_RANKINGS_SECTIONAL_ELABORATION,
    POLICE_INTERVENTION_DEATHS_RANKINGS_MUNICIPALITY_ELABORATION,
    POLICE_INTERVENTION_DEATHS_RANKINGS_NEIGHBOURHOOD,
    POLICE_INTERVENTION_DEATHS_PROFILE_BY_PEOPLE_TYPE,
    POLICE_INTERVENTION_DEATHS_PROFILE_BY_GENDER,
    POLICE_INTERVENTION_DEATHS_PROFILE_BY_AGE,
    POLICE_INTERVENTION_DEATHS_PROFILE_BY_SKIN_COLOR,
    POLICE_INTERVENTION_DEATHS_PROFILE_BY_PROFESSION,
    IML_OCURRENCES,
    IML_DEATHCAUSES
};


const OBJECTS_OCURRENCES = 'objects-ocurrences';
const OBJECTS_RANKINGS_POLICE_STATION = 'objects-rankings-police-station';
const OBJECTS_RANKINGS_DEPARTMENT = 'objects-rankings-department';
const OBJECTS_RANKINGS_SECTIONAL = 'objects-rankings-sectional';
const OBJECTS_RANKINGS_CITY = 'objects-rankings-city';
const OBJECTS_RANKINGS_NEIGHBOURHOOD = 'objects-rankings-neighbourhood';
const OBJECTS_RANKINGS_SUBTYPES = 'objects-rankings-subtypes';
const OBJECTS_RANKINGS_TYPES = 'objects-rankings-types';
const OBJECTS_RANKINGS_BRANDS = 'objects-rankings-brands';
const OBJECTS_SPOTS = 'objects-spots';
const OBJECTS_DETAILS_PRESENTATION = 'objects-details-presentation';
const OBJECTS_DETAILS_AUTORSHIP = 'objects-details-autorship';
const OBJECTS_DETAILS_FLAGRANT = 'objects-details-flagrant';
const OBJECTS_DETAILS_INFRACTIONAL = 'objects-details-infractional';
const OBJECTS_DETAILS_RUBRIC = 'objects-details-rubric';
const OBJECTS_DETAILS_CONDUCT = 'objects-details-conduct';
const OBJECTS_DETAILS_UNFOLDING = 'objects-details-unfolding';
const OBJECTS_DETAILS_CIRCUMSTANCES = 'objects-details-circumstances';
const OBJECTS_TYPE = 'object-type';
const OBJECTS_SUBTYPE = 'object-subtype';
const OBJECTS_BRAND = 'object-brand';
const OBJECTS_COLOR = 'object-color';
const OBJECTS_BLOCKSTATUS = 'object-blockstatus';
const OBJECTS_QUANTITY = 'object-quantity';

export const QUERY_KEYS_OBJECTS = {
    OBJECTS_OCURRENCES,
    OBJECTS_RANKINGS_POLICE_STATION,
    OBJECTS_RANKINGS_DEPARTMENT,
    OBJECTS_RANKINGS_SECTIONAL,
    OBJECTS_RANKINGS_CITY,
    OBJECTS_RANKINGS_NEIGHBOURHOOD,
    OBJECTS_RANKINGS_SUBTYPES,
    OBJECTS_RANKINGS_TYPES,
    OBJECTS_RANKINGS_BRANDS,
    OBJECTS_SPOTS,
    OBJECTS_DETAILS_PRESENTATION,
    OBJECTS_DETAILS_AUTORSHIP,
    OBJECTS_DETAILS_FLAGRANT,
    OBJECTS_DETAILS_INFRACTIONAL,
    OBJECTS_DETAILS_RUBRIC,
    OBJECTS_DETAILS_CONDUCT,
    OBJECTS_DETAILS_UNFOLDING,
    OBJECTS_DETAILS_CIRCUMSTANCES,
    OBJECTS_TYPE,
    OBJECTS_SUBTYPE,
    OBJECTS_BRAND,
    OBJECTS_COLOR,
    OBJECTS_BLOCKSTATUS,
    OBJECTS_QUANTITY
};


const BENEFITS_FAMILY_ALLOWANCE_TRANSFER_VALUES_FAMILIES ='family-allowance-transfer-values-families';
const BENEFITS_FAMILY_ALLOWANCE_TRANSFER_VALUES_VALUES ='family-allowance-transfer-values-values';
const BENEFITS_FAMILY_ALLOWANCE_TRANSFER_VALUES_AVERAGE_VALUES ='family-allowance-transfer-values-average-values';
const BENEFITS_FAMILY_ALLOWANCE_PAYMENTS_BENEFICIARIES ='family-allowance-payments-beneficiaries';
const BENEFITS_FAMILY_ALLOWANCE_PAYMENTS_PAYMENTS ='family-allowance-payments-payments';
const BENEFITS_OTHER_BENEFITS_BENEFIT_EVOLUTION ='other-benefits-benefit-evolution';
const BENEFITS_OTHER_BENEFITS_VALUES ='other-benefits-values';
const BENEFITS_OTHER_BENEFITS_AVERAGE_VALUES ='other-benefits-average-values';
const BENEFITS_BPC_BENEFICIARIES ='bpc-beneficiaries';
const BENEFITS_BPC_PAYMENTS ='bpc-payments';

export const QUERY_KEYS_BENEFITS = {
    BENEFITS_FAMILY_ALLOWANCE_TRANSFER_VALUES_FAMILIES,
    BENEFITS_FAMILY_ALLOWANCE_TRANSFER_VALUES_VALUES,
    BENEFITS_FAMILY_ALLOWANCE_TRANSFER_VALUES_AVERAGE_VALUES,
    BENEFITS_FAMILY_ALLOWANCE_PAYMENTS_BENEFICIARIES,
    BENEFITS_FAMILY_ALLOWANCE_PAYMENTS_PAYMENTS,
    BENEFITS_OTHER_BENEFITS_BENEFIT_EVOLUTION,
    BENEFITS_OTHER_BENEFITS_VALUES,
    BENEFITS_OTHER_BENEFITS_AVERAGE_VALUES,
    BENEFITS_BPC_BENEFICIARIES,
    BENEFITS_BPC_PAYMENTS
};